import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { KeyRound } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { getErrorMessage } from '../../lib/apiClient';
import { resetPassword } from './authApi';
import AuthShell from './AuthShell';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: { newPassword: '' } });

  const mutation = useMutation({
    mutationFn: ({ newPassword }) => resetPassword(token, newPassword),
    onSuccess: () => {
      toast.success('Password reset — please sign in');
      navigate('/login', { replace: true });
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Reset failed')),
  });

  return (
    <AuthShell
      icon={<KeyRound className="h-7 w-7 text-brand-600" />}
      title="Choose a new password"
      subtitle="Set the password for your account."
    >
      {!token ? (
        <p className="text-center text-sm text-red-600">
          This reset link is missing its token. Request a new one.
        </p>
      ) : (
        <form className="space-y-4" onSubmit={handleSubmit((v) => mutation.mutate(v))} noValidate>
          <Input
            label="New password"
            type="password"
            autoComplete="new-password"
            error={errors.newPassword?.message}
            {...register('newPassword', { required: 'Password is required' })}
          />
          <Button type="submit" className="w-full" loading={mutation.isPending}>
            Reset password
          </Button>
        </form>
      )}
      <p className="mt-6 text-center text-sm text-slate-500">
        <Link to="/forgot-password" className="font-medium text-brand-600 hover:text-brand-700">
          Request a new link
        </Link>
      </p>
    </AuthShell>
  );
}
